/*
 * $Id:: model.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import { all, call, put, takeLatest, select } from 'redux-saga/effects';

import {downloadFile, openPDFinNewTab} from '../../common/utils/service';
import { me as meSlice } from '../AuthenticatedPage/authenticationSlice';
import { SLICE_NAME } from './constants';
import {
    fetchApplicationsPaginated,
    fetchApplicationReceipt,
    fetchApplicationsExport,
    updateApplicationNote,
    duplicateApplication,
    validateEserviceInitiation,
    lockApplicationNote,
    fetchApplicationPaymentInvoice,
    fetchApplicationsFilters,
    fetchHomepageApplicationCount,
    fetchWaitingForPaymentApplicationCount,
    fetchWaitingForSignatureApplicationCount
} from './service';

function* requestMyApplications(action) {
    const me = yield select(meSlice);
    const { userId, parentUserId } = me || {};
    yield all([put({ type: `${SLICE_NAME}/loading`, payload: true }), put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(fetchApplicationsPaginated, {
        ...action.payload,
        userId: parentUserId ? parentUserId : userId,
    });
    if (!response.error) {
        yield put({
            type: `${SLICE_NAME}/list`,
            payload: response,
        });
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.description });
    }
    yield put({ type: `${SLICE_NAME}/loading`, payload: false });
}

function* getMyApplications() {
    yield takeLatest(`${SLICE_NAME}/requestMyApplications`, requestMyApplications);
}

function* requestApplicationReceipt(action) {
    yield all([put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(fetchApplicationReceipt, {
        ...action.payload,
    });
    if (!response.error) {
        openPDFinNewTab(response);
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.description });
    }
}

function* getApplicationReceipt() {
    yield takeLatest(`${SLICE_NAME}/requestApplicationReceipt`, requestApplicationReceipt);
}

function* requestMyApplicationsExport(action) {
    const { type } = action.payload.params;
    if (type && (type === 'excel' || type === 'pdf')) {
        yield all([put({ type: `${SLICE_NAME}/error`, payload: false })]);
        const response = yield call(fetchApplicationsExport, {
            ...action.payload,
        });
        if (!response.error) {
            downloadFile(response);
        } else {
            yield put({ type: `${SLICE_NAME}/error`, payload: response.error.description });
        }
    }
}

function* getMyApplicationsExport() {
    yield takeLatest(`${SLICE_NAME}/requestMyApplicationsExport`, requestMyApplicationsExport);
}

function* requestNoteUpdate(action) {
    yield all([put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(updateApplicationNote, {
        ...action.payload,
    });
    if (!response.error) {
        // update table list
        yield call(requestMyApplications, action.payload.actionParams);
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.code === 403 ? response.error : response.error.description });
    }
}

function* updateNote() {
    yield takeLatest(`${SLICE_NAME}/requestNoteUpdate`, requestNoteUpdate);
}

function* requestNoteLocking(action) {
    yield all([put({ type: `${SLICE_NAME}/loadingNoteLock`, payload: true }), put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(lockApplicationNote, {
        ...action.payload,
    });
    if (!response.error) {
        yield put({ type: `${SLICE_NAME}/noteLocked`, payload: false });
    } else {
        if (response.error.description.includes('locked')) {
            yield put({ type: `${SLICE_NAME}/noteLocked`, payload: true });
        }
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.code === 403 ? response.error : response.error.description });
    }
    yield put({ type: `${SLICE_NAME}/loadingNoteLock`, payload: false });
}

function* lockNote() {
    yield takeLatest(`${SLICE_NAME}/lockNote`, requestNoteLocking);
}

function* requestDuplicateApplication(action) {
    yield all([put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(duplicateApplication, {
        ...action.payload,
    });
    if (!response.error) {
        const redirectUrl = response.url;
        if (!!redirectUrl) {
            window.open(redirectUrl, '_blank');
        }
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.description });
    }
}

function* getDuplicateApplication() {
    yield takeLatest(`${SLICE_NAME}/requestDuplicateApplication`, requestDuplicateApplication);
}

function* requestEserviceValidation(action) {
    yield all([put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(validateEserviceInitiation, {
        ...action.payload,
    });
    if (!response.error) {
        if (response.status === 'VALID' && response.redirectUrl) window.open(response.redirectUrl, '_blank');
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error });
    }
}

function* getEserviceValidation() {
    yield takeLatest(`${SLICE_NAME}/requestEserviceValidation`, requestEserviceValidation);
}

function* requestApplicationPaymentInvoice(action) {
    yield all([put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(fetchApplicationPaymentInvoice, { ...action.payload });
    if (!response.error) {
        downloadFile(response);
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.description });
    }
}

function* getApplicationPaymentInvoice() {
    yield takeLatest(`${SLICE_NAME}/getApplicationPaymentInvoice`, requestApplicationPaymentInvoice);
}

function* requestApplicationsFilters(action) {
    yield all([put({ type: `${SLICE_NAME}/loading`, payload: true }), put({ type: `${SLICE_NAME}/error`, payload: false })]);
    const response = yield call(fetchApplicationsFilters, { ...action.payload });
    if (!response.error) {
        yield put({
            type: `${SLICE_NAME}/filters`,
            payload: response,
        });
    } else {
        yield put({ type: `${SLICE_NAME}/error`, payload: response.error.description });
    }
    yield put({ type: `${SLICE_NAME}/loading`, payload: false });
}

function* getApplicationsFilters() {
    yield takeLatest(`${SLICE_NAME}/getApplicationsFilters`, requestApplicationsFilters);
}

function* requestHomepageApplicationCountSaga(action) {
    const response = yield call(fetchHomepageApplicationCount);
    if (!response.error) {
        yield put({
            type: `${SLICE_NAME}/listTotalCount`,
            payload: response,
        });
    } else {
        console.log(response.error.description);
    }
}

function* requestWaitingForSignatureApplicationCountSaga(action) {
    const response = yield call(fetchWaitingForSignatureApplicationCount);
    if (!response.error) {
        yield put({
            type: `${SLICE_NAME}/waitingForSignatureCount`,
            payload: response,
        });
    } else {
        console.log(response.error.description);
    }
}

function* requestWaitingForPaymentApplicationCountSaga(action) {
    const response = yield call(fetchWaitingForPaymentApplicationCount);
    if (!response.error) {
        yield put({
            type: `${SLICE_NAME}/waitingForPaymentCount`,
            payload: response,
        });
    } else {
        console.log(response.error.description);
    }
}


function* getHomepageApplicationCount() {
    yield takeLatest(`${SLICE_NAME}/getHomepageApplicationCount`, requestHomepageApplicationCountSaga);
}

function* getWaitingForSignatureApplicationCount() {
    yield takeLatest(`${SLICE_NAME}/getWaitingForSignatureApplicationCount`, requestWaitingForSignatureApplicationCountSaga);
}

function* getWaitingForPaymentApplicationCount() {
    yield takeLatest(`${SLICE_NAME}/getWaitingForPaymentApplicationCount`, requestWaitingForPaymentApplicationCountSaga);
}

export {
    getMyApplications,
    getApplicationReceipt,
    getMyApplicationsExport,
    updateNote,
    lockNote,
    getDuplicateApplication,
    getEserviceValidation,
    getApplicationPaymentInvoice,
    getApplicationsFilters,
    getHomepageApplicationCount,
    getWaitingForSignatureApplicationCount,
    getWaitingForPaymentApplicationCount,
};
